/* SIP Connect Guide
 * 
 * Full Czech walkthrough for connecting Zoiper, Yak, Linphone or any
 * SIP softphone to this PBX. Loads user's own credentials from
 * /api/softphone/connect-guide (audit-logged) and renders copy-to-clip
 * fields + step-by-step instructions per app + QR code for one-tap
 * import on mobile.
 */
import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { useToast } from '@/hooks/use-toast';
import { Copy, Eye, EyeSlash, Globe, DeviceMobile, MonitorPlay, WarningCircle, CheckCircle, ArrowRight } from '@phosphor-icons/react';

const CopyField = ({ label, value, mono = true, testid, hidden: hiddenProp = false }) => {
    const [revealed, setRevealed] = useState(!hiddenProp);
    const { toast } = useToast();
    const copy = () => {
        navigator.clipboard.writeText(String(value || ''));
        toast({ title: 'Zkopírováno', description: label });
    };
    return (
        <div className="flex flex-col gap-1" data-testid={testid}>
            <label className="text-[10px] uppercase tracking-[0.15em] font-bold"
                   style={{ color: 'var(--text-secondary)' }}>{label}</label>
            <div className="flex items-stretch gap-1">
                <input
                    readOnly
                    type={revealed ? 'text' : 'password'}
                    value={value || ''}
                    onFocus={(e) => e.target.select()}
                    className={`flex-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm ${mono ? 'font-mono' : ''}`}
                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                />
                {hiddenProp && (
                    <button onClick={() => setRevealed(!revealed)}
                            className="px-2 border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                            title={revealed ? 'Skrýt' : 'Zobrazit'}
                            data-testid={`${testid}-toggle`}>
                        {revealed ? <EyeSlash size={14} /> : <Eye size={14} />}
                    </button>
                )}
                <button onClick={copy}
                        className="px-2 border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
                        title="Kopírovat"
                        data-testid={`${testid}-copy`}>
                    <Copy size={14} />
                </button>
            </div>
        </div>
    );
};

const Step = ({ n, title, children }) => (
    <div className="flex gap-3">
        <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center font-mono font-bold"
             style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
            {n}
        </div>
        <div className="flex-1 pt-0.5">
            <div className="font-semibold text-sm mb-0.5">{title}</div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{children}</div>
        </div>
    </div>
);

const SipConnect = () => {
    const [guide, setGuide] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('zoiper');
    const { toast } = useToast();

    useEffect(() => {
        api.get('/softphone/connect-guide')
            .then(r => setGuide(r.data))
            .catch(e => setError(e.response?.data?.detail || 'Chyba načítání'));
    }, []);

    if (error) {
        return (
            <div className="space-y-6" data-testid="sip-connect-page">
                <PageHeader
                    eyebrow="Telefonie"
                    title="Připojení telefonu — Setup návod"
                    description="Vaše SIP účet pro Zoiper / Yak / Linphone / jakýkoliv SIP softphone."
                />
                <Card>
                    <div className="p-6 flex items-start gap-3">
                        <WarningCircle size={20} className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                        <div>
                            <div className="font-semibold mb-1">Nemůžu načíst vaše SIP údaje</div>
                            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{error}</div>
                            <div className="text-xs mt-3" style={{ color: 'var(--text-secondary)' }}>
                                Potřebujete přiřazenou klapku v <a href="/extensions" className="underline">SIP Účty</a> a vygenerované heslo.
                                Pokud nejste admin, kontaktujte správce systému.
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (!guide) {
        return <div className="p-8 text-sm" style={{ color: 'var(--text-secondary)' }}>Načítám…</div>;
    }

    const c = guide.sip_credentials;
    const p = guide.pbx;
    const w = guide.webrtc;

    return (
        <div className="space-y-6" data-testid="sip-connect-page">
            <PageHeader
                eyebrow={`Klapka ${guide.user.extension} · ${guide.user.display_name}`}
                title="Připojení telefonu — Setup návod"
                description="Vaše osobní SIP účet. Nainstalujte si aplikaci (zdarma), naskenujte QR nebo zkopírujte údaje níže. Když budete přihlášení, příchozí hovory na firmu zazvoní jako první na váš mobil přes SIP, pak teprve na GSM."
            />

            {/* Quick ringing-order explanation */}
            <Card>
                <div className="p-4 flex flex-wrap items-center gap-3 text-sm">
                    <div className="flex items-center gap-1.5 font-semibold" style={{ color: 'var(--brand)' }}>
                        <CheckCircle size={18} weight="bold" /> Pořadí vyzvánění:
                    </div>
                    <div className="flex items-center gap-2 font-mono text-xs">
                        <span className="px-2 py-1" style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>
                            1. SIP klapka {guide.user.extension} (Zoiper/Yak)
                        </span>
                        <ArrowRight size={14} />
                        <span className="px-2 py-1" style={{ background: 'rgba(59,130,246,0.15)', color: '#3b82f6' }}>
                            25 s
                        </span>
                        <ArrowRight size={14} />
                        <span className="px-2 py-1" style={{ background: 'rgba(168,85,247,0.15)', color: '#a855f7' }}>
                            2. GSM mobilní číslo (přes Fayn)
                        </span>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT: Credentials */}
                <Card title="Vaše SIP údaje" className="lg:col-span-2">
                    <div className="p-4 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <CopyField label="Uživatelské jméno (Username / Auth ID)"
                                       value={c.sip_username} testid="sip-username" />
                            <CopyField label="Heslo (Password)" value={c.password}
                                       testid="sip-password" hidden />
                            <CopyField label="Doména / Server (Domain / Host)"
                                       value={c.domain} testid="sip-domain" />
                            <CopyField label="Zobrazované jméno (Display name)"
                                       value={c.display_name} mono={false} testid="sip-display-name" />
                        </div>
                        <div className="grid grid-cols-3 gap-3 pt-2 border-t" style={{ borderColor: 'var(--border-default)' }}>
                            <CopyField label="UDP Port" value={p.udp_port} testid="sip-udp-port" />
                            <CopyField label="TCP Port" value={p.tcp_port} testid="sip-tcp-port" />
                            <CopyField label="TLS Port (SRTP)" value={p.tls_port} testid="sip-tls-port" />
                        </div>
                        <div className="text-xs flex flex-wrap items-center gap-3 pt-2"
                             style={{ color: 'var(--text-secondary)' }}>
                            <span><strong>Transport:</strong> {p.preferred_transport} (doporučeno)</span>
                            <span>·</span>
                            <span><strong>Kodeky:</strong> {p.codecs_preferred.join(', ')}</span>
                        </div>
                    </div>
                </Card>

                {/* RIGHT: QR */}
                <Card title="QR pro mobil (one-tap import)">
                    <div className="p-4 flex flex-col items-center gap-3">
                        <div style={{ background: '#fff', padding: 16, borderRadius: 4 }}>
                            <QRCodeSVG
                                value={guide.provisioning_uris.linphone}
                                size={180}
                                level="M"
                                data-testid="sip-qr-linphone"
                            />
                        </div>
                        <div className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                            Naskenujte v mobilu kamerou (iOS) nebo Linphone aplikací (Android).
                            QR obsahuje formát: <code>sip:user:pass@host:port</code>
                        </div>
                        <a href={guide.provisioning_uris.zoiper}
                           data-testid="zoiper-deeplink"
                           className="text-xs underline" style={{ color: 'var(--brand)' }}>
                            Otevřít přímo v Zoiperu (pokud je nainstalovaný)
                        </a>
                    </div>
                </Card>
            </div>

            {/* Tabs: per-app setup */}
            <Card title="Návod krok-za-krokem">
                <div className="border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <div className="flex">
                        {[
                            { id: 'zoiper', label: 'Zoiper (iOS / Android)', icon: DeviceMobile },
                            { id: 'yak', label: 'Yak (iOS / Android)', icon: DeviceMobile },
                            { id: 'linphone', label: 'Linphone', icon: DeviceMobile },
                            { id: 'browser', label: 'V prohlížeči (žádná instalace)', icon: MonitorPlay },
                        ].map(t => (
                            <button key={t.id}
                                    onClick={() => setTab(t.id)}
                                    data-testid={`sip-tab-${t.id}`}
                                    className="px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] inline-flex items-center gap-1.5 border-b-2"
                                    style={{
                                        borderColor: tab === t.id ? 'var(--brand)' : 'transparent',
                                        color: tab === t.id ? 'var(--brand)' : 'var(--text-secondary)',
                                    }}>
                                <t.icon size={12} weight="bold" /> {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    {tab === 'zoiper' && (
                        <>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <strong>Zoiper</strong> je nejstabilnější bezplatný SIP klient pro iOS i Android. Free verze stačí (placená Pro odemyká kodek G.729 a echo cancellation, není nutné).
                            </p>
                            <Step n={1} title="Nainstalujte Zoiper">
                                App Store / Google Play → &quot;<strong>Zoiper</strong>&quot; → Free verze (Zoiper5 nebo Zoiper IAX).
                            </Step>
                            <Step n={2} title="Přidat účet">
                                Otevřete → <strong>Settings → Accounts → +</strong> → <strong>SIP</strong>. Nebo na úvodní obrazovce klikněte &quot;Set up new account&quot;.
                            </Step>
                            <Step n={3} title="Vyplňte údaje (viz pole nahoře)">
                                <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs font-mono">
                                    <div>Username: <strong>{c.sip_username}</strong></div>
                                    <div>Password: <strong>••••••••</strong> (zkopírujte)</div>
                                    <div>Domain / Host: <strong>{c.domain}</strong></div>
                                    <div>Outbound proxy: <strong>{c.domain}:{p.udp_port}</strong></div>
                                </div>
                            </Step>
                            <Step n={4} title="Transport: UDP, Port: 5060">
                                Klikněte <strong>Network → Transport → UDP</strong>. Port nechte default 5060. (Pokud UDP nejde, vyzkoušejte TCP 5060 nebo TLS 5061.)
                            </Step>
                            <Step n={5} title="Kodeky">
                                <strong>Audio codecs</strong>: nahoru posuňte <strong>OPUS</strong>, <strong>G.722</strong>, <strong>PCMA</strong>, <strong>PCMU</strong>. Ostatní vypněte.
                            </Step>
                            <Step n={6} title="Push notifications (iOS)">
                                <strong>Settings → General → Background App Refresh: ON</strong>. Zoiper Pro má vlastní VoIP push (doporučeno).
                            </Step>
                            <Step n={7} title="Test">
                                Zelený puntík u účtu = registrováno ✓. Vytočte na klávesnici <strong>*43</strong> (echo test) nebo si nechte zavolat z mobilu.
                            </Step>
                        </>
                    )}

                    {tab === 'yak' && (
                        <>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <strong>Yak</strong> (od FreeSWITCH komunity, dříve &quot;Acrobits Softphone Pro&quot;) je vyspělý placený softphone pro iOS / Android. Free trial 7 dní, plná verze ~5 €.
                            </p>
                            <Step n={1} title="Nainstalujte Yak">
                                App Store / Google Play → &quot;<strong>Yak softphone</strong>&quot;.
                            </Step>
                            <Step n={2} title="Přidat SIP účet">
                                Spusťte → <strong>Add Account</strong> → <strong>SIP / General SIP Account</strong>.
                            </Step>
                            <Step n={3} title="Vyplňte stejné údaje jako v Zoiperu">
                                <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs font-mono">
                                    <div>Username: <strong>{c.sip_username}</strong></div>
                                    <div>Password: <strong>••••••••</strong></div>
                                    <div>Host: <strong>{c.domain}</strong></div>
                                    <div>Authentication username: <strong>{c.sip_username}</strong></div>
                                </div>
                            </Step>
                            <Step n={4} title="Transport + NAT keep-alive">
                                Advanced → Transport: <strong>UDP</strong> · NAT keep-alive: <strong>20 s</strong> · Register every: <strong>3600 s</strong>.
                            </Step>
                            <Step n={5} title="Push notifications">
                                Settings → <strong>Push notifications: Enabled</strong> (Yak má vlastní push servery, fungue spolehlivě i v pohotovostním režimu).
                            </Step>
                        </>
                    )}

                    {tab === 'linphone' && (
                        <>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <strong>Linphone</strong> — open-source, zdarma na iOS / Android / desktop. Načte vaše údaje z QR kódu výše.
                            </p>
                            <Step n={1} title="Nainstalujte Linphone">
                                App Store / Google Play / linphone.org/downloads → &quot;<strong>Linphone</strong>&quot;.
                            </Step>
                            <Step n={2} title="Use a SIP account">
                                Při prvním spuštění zvolte &quot;<strong>Use a SIP account</strong>&quot; (NE &quot;Create account&quot;).
                            </Step>
                            <Step n={3} title="Buď QR nebo manuálně">
                                Klikněte ikonu QR v rohu → naskenujte QR kód výše. <em>NEBO</em> vyplňte:
                                <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs font-mono">
                                    <div>Username: <strong>{c.sip_username}</strong></div>
                                    <div>Password: <strong>••••••••</strong></div>
                                    <div>Domain: <strong>{c.domain}</strong></div>
                                    <div>Transport: <strong>UDP</strong></div>
                                </div>
                            </Step>
                            <Step n={4} title="Test">
                                Zelený puntík v horní liště = registrováno. Vytočte interní klapku (např. recepci) nebo *43 pro echo test.
                            </Step>
                        </>
                    )}

                    {tab === 'browser' && (
                        <>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                <strong>WebRTC softphone</strong> — žádná instalace. Funguje na desktopu i mobilu, ovládáte přes prohlížeč.
                            </p>
                            <Step n={1} title="Otevřete /softphone">
                                V tomto CMS klikněte v levém menu na &quot;<strong>Softphone (SIP)</strong>&quot;. Aplikace se automaticky přihlásí vaším účtem.
                            </Step>
                            <Step n={2} title="Povolte mikrofon">
                                Prohlížeč se zeptá na přístup k mikrofonu. Povolte. Bez toho hovor nepůjde.
                            </Step>
                            <Step n={3} title="WSS adresa (pro pokročilé)">
                                Pokud byste chtěli použít externí WebRTC klient: <code>{w.wss_url}</code>
                            </Step>
                            <Step n={4} title="STUN/TURN">
                                STUN: <code>{(w.stun_servers || []).join(', ')}</code>. TURN: {w.turn_url || '— nenakonfigurován —'}
                            </Step>
                        </>
                    )}
                </div>
            </Card>

            {/* Tips */}
            <Card title="Tipy a varování">
                <div className="p-4 space-y-3 text-sm">
                    <div className="flex gap-2 items-start">
                        <CheckCircle size={16} weight="bold" className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                        <div style={{ color: 'var(--text-secondary)' }}>{guide.tips.ringing_order}</div>
                    </div>
                    <div className="flex gap-2 items-start">
                        <WarningCircle size={16} weight="bold" className="flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
                        <div style={{ color: 'var(--text-secondary)' }}>{guide.tips.mobile_data_warning}</div>
                    </div>
                    <div className="flex gap-2 items-start">
                        <WarningCircle size={16} weight="bold" className="flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
                        <div style={{ color: 'var(--text-secondary)' }}>{guide.tips.battery_warning}</div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default SipConnect;
